import { CURRENT_CLIENT } from '@/constants'
import type { LandingCta } from '@/types/client-landing'

/** Resolve at render time via {@link resolveJobsBoardPublicUrl}. */
export const JOBS_BOARD_CTA_SENTINEL = 'jobs-board:'

export function isPublicJobsBoardEnabled(): boolean {
  const features = (CURRENT_CLIENT as { features?: { publicJobs?: { enabled?: boolean } } }).features
  return features?.publicJobs?.enabled === true
}

const MARKETING_THEME_KEY = 'yeshuman-marketing-theme'

export type JobsBoardMarketingTheme = 'light' | 'dark'

function resolveJobsBoardTheme(theme?: string | null): JobsBoardMarketingTheme | null {
  if (theme === 'dark' || theme === 'light') return theme
  if (theme === 'system' && typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return null
}

/** Pass Labs session theme to jobs board on another origin (sessionStorage is not shared). */
export function appendJobsBoardMarketingTheme(
  url: string,
  theme?: JobsBoardMarketingTheme | 'system' | null
): string {
  if (typeof window === 'undefined') return url
  try {
    const fromArg = resolveJobsBoardTheme(theme ?? null)
    const fromSession = resolveJobsBoardTheme(sessionStorage.getItem(MARKETING_THEME_KEY))
    const resolved = fromArg ?? fromSession
    if (!resolved) return url
    const next = new URL(url, window.location.origin)
    next.searchParams.set('marketingTheme', resolved)
    return next.toString()
  } catch {
    return url
  }
}

/** Labs UI → jobs SSR board URL. Env override wins over client-config. */
export function resolveJobsBoardPublicUrl(theme?: JobsBoardMarketingTheme | null): string | null {
  if (!isPublicJobsBoardEnabled()) return null
  const env = import.meta.env.VITE_PUBLIC_JOBS_BOARD_URL?.trim()
  const base = env
    ? env.replace(/\/$/, '')
    : (CURRENT_CLIENT as { jobsBoard?: { publicUrl?: string } }).jobsBoard?.publicUrl?.trim()?.replace(/\/$/, '')
  if (!base) return null
  return appendJobsBoardMarketingTheme(base, theme)
}

export function resolveLandingCta(
  cta?: LandingCta,
  theme?: JobsBoardMarketingTheme | null
): LandingCta | undefined {
  if (!cta) return undefined
  if (cta.to !== JOBS_BOARD_CTA_SENTINEL) return cta
  const url = resolveJobsBoardPublicUrl(theme)
  if (!url) return undefined
  return { ...cta, to: url }
}
